import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { css } from "@emotion/react";
import { transferToNative } from "@src/lib/WebAppBridge";
import useCallToken from "@src/hooks/useCallToken";
import ToastMessage from "@src/components/filter/ToastMessage";
import { useAiBottomSheet } from "@src/hooks/filter/useAiBottomSheet";
import { useFavoriteRegionStore } from "./store/favoriteRegionStore";
import { useGetFavoriteRegionFilter, usePostFavoriteRegionFilter } from "./service/api";
import type { FavoriteRegionSlot } from "./store/favoriteRegionStore";
import AiResetConfirmModal from "@src/pages/aiFilter/AiResetConfirmModal";
import FavoriteRegionHeader from "./ui/FavoriteRegionHeader";
import LocationFirstStep from "./ui/LocationFirstStep";
import PurposeSecondStep from "./ui/PurposeSecondStep";
import BottomSheet from "./ui/BottomSheet";
import SelectedRegionSummary from "./ui/SelectedRegionSummary";
import FavoriteRegionFooter from "./ui/FavoriteRegionFooter";
import { useMajorCategory } from "@src/pages/mapFilter/useFilterList";

const filterList = [{ id: 1 }, { id: 2 }, { id: 3 }];

const FavoriteRegion = () => {
    const { sheetRef, content, buttonWrapper, sort, buttonRef1, buttonRef2, buttonRef3, onScrollDown, checkShow, onScrollUp } =
        useAiBottomSheet(90);

    const filters = useFavoriteRegionStore((s) => s.filters);
    const checkedFilter = useFavoriteRegionStore((s) => s.checkedFilter);
    const setFilterForSlot = useFavoriteRegionStore((s) => s.setFilterForSlot);
    const setFilters = useFavoriteRegionStore((s) => s.setFilters);
    const setCheckedFilter = useFavoriteRegionStore((s) => s.setCheckedFilter);
    const setSlotFinish = useFavoriteRegionStore((s) => s.setSlotFinish);
    const resetFilter = useFavoriteRegionStore((s) => s.resetFilter);

    const currentFilter = filters[checkedFilter - 1];
    const slotIndex = (checkedFilter - 1) as 0 | 1 | 2;

    const [step, setStep] = useState(1);
    const [listVisible, setListVisible] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [showSaveSuccessToast, setShowSaveSuccessToast] = useState(false);
    const [showSortBox, setShowSortBox] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [isEditingCondition, setIsEditingCondition] = useState(false);
    const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);

    const queryClient = useQueryClient();
    const { data: savedFilter, isLoading: isFilterLoading } = useGetFavoriteRegionFilter(true);
    const didInitDefaultSlotRef = useRef(false);

    const isSlotComplete = currentFilter?.isFinish ?? false;
    const showStepContent = !isSlotComplete || isEditingCondition;
    const postFilter = usePostFavoriteRegionFilter();
    const expandMajorCategory = (cats: string[]) => {
        const first = cats?.[0];
        if (!first) return [];
        const major = useMajorCategory.find((m) => m.value === first);
        return major?.inputValue?.length ? major.inputValue : cats;
    };
    const collapseMajorCategory = (cats: string[]) => {
        if (!cats?.length) return [];
        const set = new Set(cats);
        const major = useMajorCategory.find(
            (m) => m.value && m.inputValue?.length && m.inputValue.length === set.size && m.inputValue.every((v) => set.has(v)),
        );
        return major ? [major.value] : cats;
    };

    const isJejuCortar = (cortar: string) => cortar.startsWith("50");
    const hasSigunguOrBelow = (cortar: string) => cortar.slice(2) !== "00000000";
    const hasEmdForJeju = (cortar: string) => cortar.slice(5) !== "00000";
    const canGoNextStep1 =
        currentFilter.regions.length > 0 &&
        currentFilter.regions.every((cortar) => {
            if (!hasSigunguOrBelow(cortar)) return false;
            return isJejuCortar(cortar) ? hasEmdForJeju(cortar) : true;
        });
    const canGoNextStep2 = currentFilter.categories.length > 0;
    const canGoNext = step === 1 ? canGoNextStep1 : step === 2 ? canGoNextStep2 : true;

    useEffect(() => {
        if (!savedFilter?.list?.length) return;
        const list = savedFilter.list;
        const nextFilters: [FavoriteRegionSlot, FavoriteRegionSlot, FavoriteRegionSlot] = [0, 1, 2].map((slotIndex) => {
            const item = list.find((x) =>
                x.settingOrder !== undefined ? x.settingOrder === slotIndex + 1 : x.order === slotIndex + 1,
            );
            if (!item?.itemFilter) {
                return { regions: [], categories: [], isFinish: false };
            }
            const regions = item.itemFilter.regions ?? [];
            const categories = collapseMajorCategory(item.itemFilter.categories ?? []);
            const hasContent = regions.length > 0 || categories.length > 0;
            return {
                regions,
                categories,
                isFinish: item.isFinish ?? hasContent,
                floor: item.itemFilter.floor ?? null,
            };
        }) as [FavoriteRegionSlot, FavoriteRegionSlot, FavoriteRegionSlot];
        setFilters(nextFilters);
    }, [savedFilter, setFilters]);

    useEffect(() => {
        if (didInitDefaultSlotRef.current) return;
        if (!savedFilter?.list?.length) return;
        const hasAnyFinished = savedFilter.list.some((x) => x?.isFinish === true);
        if (!hasAnyFinished) return;

        didInitDefaultSlotRef.current = true;
        setCheckedFilter(1);
        setIsEditingCondition(false);
        setStep(1);
        setListVisible(true);
        onScrollDown();
    }, [savedFilter?.list, onScrollDown, setCheckedFilter]);

    const onClickGoBack = () => {
        transferToNative("done", "closeWebView");
    };

    const handleStep2Next = async () => {
        if (postFilter.isLoading) return;
        const currentChecked = useFavoriteRegionStore.getState().checkedFilter;
        const slotIndexToSave = (currentChecked - 1) as 0 | 1 | 2;
        setSlotFinish(slotIndexToSave, true);
        const sortVal = useFavoriteRegionStore.getState().sortType ?? 0;
        const currentFilters = useFavoriteRegionStore.getState().filters;
        const slot = currentFilters[slotIndexToSave];
        const categoriesForApi = expandMajorCategory(slot.categories);
        const shouldSendFloor = categoriesForApi.includes("D02");
        try {
            await postFilter.mutateAsync({
                regions: slot.regions,
                categories: categoriesForApi,
                settingOrder: currentChecked,
                isFinish: true,
                sortType: sortVal,
                saleMethod: 1,
                floor: shouldSendFloor ? slot.floor ?? null : null,
            });
            setSlotFinish(slotIndexToSave, true);
            setIsEditingCondition(false);
            setStep(1);
            setShowSaveSuccessToast(true);
            setListVisible(true);
            onScrollUp();
            queryClient.invalidateQueries(["favoriteRegionFilter"]);
            queryClient.invalidateQueries(["favoriteRegionItems"]);
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteCondition = async () => {
        if (postFilter.isLoading) return;
        const currentChecked = useFavoriteRegionStore.getState().checkedFilter;
        const slotIndexToDelete = (currentChecked - 1) as 0 | 1 | 2;
        try {
            await postFilter.mutateAsync({
                regions: [],
                categories: [],
                settingOrder: currentChecked,
                isFinish: false,
                saleMethod: 1,
                floor: null,
            });
            setFilterForSlot(slotIndexToDelete, { regions: [], categories: [], floor: null });
            setSlotFinish(slotIndexToDelete, false);
            setListVisible(false);
            setStep(1);
            setIsEditingCondition(false);
            setShowDeleteSuccessToast(true);
            queryClient.invalidateQueries(["favoriteRegionFilter"]);
            queryClient.invalidateQueries(["favoriteRegionItems"]);
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <>
            <div css={rootStyle}>
                {isFilterLoading && <div css={loadingOverlayStyle} aria-hidden />}
                <div css={rootBoxStyle} style={showStepContent ? { paddingBottom: 90 } : undefined}>
                    <FavoriteRegionHeader onClickBack={onClickGoBack} />
                    <div css={filterListContainer}>
                        {filterList.map((ele) => {
                            const isFinish = filters[ele.id - 1]?.isFinish ?? false;
                            const isChecked = checkedFilter === ele.id;
                            return (
                                <button
                                    key={ele.id}
                                    type="button"
                                    css={settingList(isFinish, isChecked)}
                                    onClick={() => {
                                        const nextId = ele.id as 1 | 2 | 3;
                                        if (nextId !== checkedFilter) {
                                            const currentSlotIndex = (checkedFilter - 1) as 0 | 1 | 2;
                                            const currentSlotFinished = filters[checkedFilter - 1]?.isFinish === true;
                                            if (!currentSlotFinished) {
                                                setFilterForSlot(currentSlotIndex, { regions: [], categories: [], floor: null });
                                                setSlotFinish(currentSlotIndex, false);
                                            }
                                            setStep(1);
                                            setIsEditingCondition(false);
                                            setCheckedFilter(nextId);
                                            const nextTabHasCondition = filters[nextId - 1]?.isFinish === true;
                                            if (nextTabHasCondition) {
                                                setListVisible(true);
                                                onScrollDown();
                                            } else {
                                                setListVisible(false);
                                                onScrollDown();
                                            }
                                        }
                                    }}
                                    id={`filterButton${ele.id}`}
                                >
                                    조건 설정 {ele.id} {isFinish ? "완료" : "추가"}
                                </button>
                            );
                        })}
                    </div>
                    {!isEditingCondition && (
                        <SelectedRegionSummary
                            slot={currentFilter}
                            onEdit={
                                isSlotComplete && !showStepContent
                                    ? () => {
                                          queryClient.removeQueries({
                                              queryKey: ["favoriteRegionItems"],
                                              predicate: (query) => query.queryKey[1] === checkedFilter,
                                          });
                                          setIsEditingCondition(true);
                                          setStep(1);
                                          setListVisible(false);
                                          onScrollDown();
                                      }
                                    : undefined
                            }
                            onDelete={isSlotComplete && !showStepContent ? handleDeleteCondition : undefined}
                        />
                    )}
                    {showStepContent && step === 1 && (
                        <LocationFirstStep
                            locationList={currentFilter.regions}
                            onChangeLocation={(val) => setFilterForSlot(slotIndex, { regions: val })}
                            onSubmitShowToast={() => setShowToast(true)}
                        />
                    )}
                    {showStepContent && step === 2 && (
                        <PurposeSecondStep
                            categories={currentFilter.categories}
                            floor={currentFilter.floor ?? null}
                            onChangeCategories={(_, val) => setFilterForSlot(slotIndex, { categories: val })}
                            onChangeFloor={(floor) => setFilterForSlot(slotIndex, { floor })}
                        />
                    )}
                    {showStepContent && (
                        <div css={stepFooterWrapperStyle}>
                            <FavoriteRegionFooter
                                step={step}
                                isPrev={step === 2}
                                isNext={step <= 2}
                                isShowList={false}
                                canGoNext={canGoNext}
                                isSaving={postFilter.isLoading}
                                isReset={false}
                                onClickPrev={() => setStep(1)}
                                onClickNext={() => {
                                    if (step === 1) setStep(2);
                                    else if (step === 2) handleStep2Next();
                                }}
                                onClickShowList={() => {}}
                                style={{ position: "relative", bottom: "auto", width: "100%", marginLeft: 0, maxWidth: "100%" }}
                            />
                        </div>
                    )}
                    <div style={{ padding: "0px 14px", marginTop: "8px" }}>
                        <BottomSheet
                            hideSheet={showStepContent}
                            sheetRef={sheetRef}
                            contentRef={content}
                            buttonWrapperRef={buttonWrapper}
                            sortRef={sort}
                            buttonRef1={buttonRef1}
                            buttonRef2={buttonRef2}
                            buttonRef3={buttonRef3}
                            step={step}
                            listVisible={listVisible}
                            checkShow={checkShow}
                            onScrollUp={onScrollUp}
                            onScrollDown={onScrollDown}
                            setStep={setStep}
                            onStep2Next={handleStep2Next}
                            isSaving={postFilter.isLoading}
                            showStepFooter={false}
                            showSortBox={showSortBox}
                            onChangeShowSortBox={() => setShowSortBox((p) => !p)}
                            onClickReset={() => setShowResetModal(true)}
                            isEditingCondition={isEditingCondition}
                        />
                    </div>
                </div>
            </div>
            <ToastMessage visible={showSaveSuccessToast} setToastVisible={setShowSaveSuccessToast}>
                조건 설정이 완료되었습니다.
            </ToastMessage>
            <ToastMessage visible={showDeleteSuccessToast} setToastVisible={setShowDeleteSuccessToast}>
                조건이 삭제되었습니다.
            </ToastMessage>
            {showResetModal && (
                <AiResetConfirmModal
                    text="필터를 초기화할까요?"
                    onClickCancel={() => setShowResetModal(false)}
                    onClickConfirm={() => {
                        resetFilter();
                        setStep(1);
                        setListVisible(false);
                        setShowResetModal(false);
                    }}
                />
            )}
        </>
    );
};

export default FavoriteRegion;

const rootBoxStyle = css`
    position: fixed;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

const rootStyle = css`
    overflow: hidden;
    height: 100%;
`;

const loadingOverlayStyle = css`
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: #fff;
`;

const filterListContainer = css`
    display: grid;
    width: 100%;
    padding: 0px 14px;
    font-size: 14px;
    gap: 4%;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 25px;
`;

const stepFooterWrapperStyle = css`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 14px;
    padding-bottom: max(14px, env(safe-area-inset-bottom, 14px));
    border-top: 6px solid #f0f0f0;
    background: #fff;
    z-index: 10;
`;

const settingList = (finish: boolean, checked: boolean) => css`
    padding: 6px 2px;
    text-align: center;
    font-size: 14px;
    border-radius: 50px;
    border: ${(finish && checked) || checked ? "1px solid #0047ff" : finish ? "1px solid #c5ffe9" : "1px solid #dadada"};
    color: ${(finish && checked) || checked ? "#0037ff" : finish ? "#0047ff" : "#dadada"};
    background-color: ${finish && checked ? "#c5ffe9" : finish ? "#c5ffe9" : ""};
`;
